var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/components/x-buildify-global-settings.ts
import { logger } from "@adornis/base/logging.js";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-hr.js";
import "@adornis/chemistry/elements/components/x-loader";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import "@adornis/chemistry/elements/components/x-stack.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { XNativeDialog } from "@adornis/dialog/x-native-dialog.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { FormController } from "@adornis/forms/x-form-controller.js";
import "@adornis/forms/x-input.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { distinctUntilChanged, filter, firstValueFrom, switchMap, takeUntil, timer } from "rxjs";
import {
  BuildifyGlobalSettingBorderDefinition,
  BuildifyGlobalSettingBorderRadiusDefinition,
  BuildifyGlobalSettings,
  BuildifyGlobalSettingSpacingDefinition,
  subscribeBuildifyGlobalSettings
} from "../../db/GlobalSettings.js";
import "./x-buildify-color-picker.js";
import { drawerWrapper } from "./x-buildify-drawer-wrapper.js";
import "./x-buildify-spacing-picker.js";
import "./x-buildify-width-picker.js";
var ItemShadow = class extends ChemistryLitElement {
  render() {
    return html` <slot></slot> `;
  }
};
ItemShadow = __decorateClass([
  customElement("item-shadow")
], ItemShadow);
var XBuildifyGlobalSettings = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this._globalSettingsClassName = new RXController(this, void 0);
    this._globalSettings = new RXController(
      this,
      this._globalSettingsClassName.observable.pipe(
        filter(Boolean),
        distinctUntilChanged(),
        switchMap(
          (className) => subscribeBuildifyGlobalSettings(className)(selectionSet(() => BuildifyGlobalSettings, 10))
        ),
        takeUntil(this.disconnected)
      )
    );
    this._form = new FormController(this, this._globalSettings.observable, {
      success: async (settings) => {
        console.log("successfully validated", settings);
        try {
          await XNativeDialog.waitFor(Promise.all([settings.save(), firstValueFrom(timer(250))]));
          void XSnackbar.show("Globale Einstellungen wurde erfolgreich aktualisiert.");
          this.dispatchEvent(new CustomEvent("close"));
        } catch (err) {
          logger.error({ err }, "Es gab ein Problem beim Speichern der Globalen Einstellungen");
          void XNativeDialog.alert(
            "Es gab ein Problem beim Speichern der Globalen Einstellungen. Bitte versuchen Sie es sp\xE4ter erneut."
          );
        }
      }
    });
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    if (this.globalSettingsClassName !== this._globalSettingsClassName.value)
      this._globalSettingsClassName.next(this.globalSettingsClassName);
  }
  render() {
    if (!this._globalSettings.value || !this._form.document)
      return html` <x-loader></x-loader> `;
    return xComponents(
      drawerWrapper({
        title: "Globale Einstellungen",
        content: () => html`
            <x-stack>
              <x-flex>
                <stack-item name="Farben">
                  <x-buildify-global-settings-color-picker
                    ${this._form.field("colors")}
                  ></x-buildify-global-settings-color-picker>
                </stack-item>

                <x-flex>
                  <stack-item name="Abstände">
                    <x-buildify-global-settings-spacing-picker
                      ${this._form.field("spacings")}
                    ></x-buildify-global-settings-spacing-picker>
                  </stack-item>
                </x-flex>

                <stack-item name="Border Radius">
                  <x-buildify-global-settings-border-radius-picker
                    ${this._form.field("borderRadius")}
                  ></x-buildify-global-settings-border-radius-picker>
                </stack-item>

                <stack-item name="Border">
                  <x-buildify-global-settings-border-picker
                    ${this._form.field("border")}
                  ></x-buildify-global-settings-border-picker>
                </stack-item>

                <stack-item name="Breiten definieren">
                  <x-buildify-global-settings-width-picker
                    ${this._form.field("widths")}
                  ></x-buildify-global-settings-width-picker>
                </stack-item>

                <stack-item name="Maximale Seitenbreite">
                  <x-buildify-spacing-picker ${this._form.field("sectionWidth")}></x-buildify-spacing-picker>
                </stack-item>

                ${this.customGlobalSettings()}
              </x-flex>
            </x-stack>
          `,
        cancel: () => {
          this._globalSettings.value?.revert();
          this.dispatchEvent(new CustomEvent("close"));
        },
        validate: () => this._form.validate(),
        options: {
          saveIcon: this.configuration.icons.save
        }
      })
    );
  }
  customGlobalSettings() {
    return nothing;
  }
  get configuration() {
    return {
      icons: {
        save: "save"
      }
    };
  }
  styles() {
    return [...super.styles(), { ":host": { height: "100%" } }];
  }
};
__decorateClass([
  property({ attribute: false })
], XBuildifyGlobalSettings.prototype, "globalSettingsClassName", 2);
__decorateClass([
  state()
], XBuildifyGlobalSettings.prototype, "_globalSettings", 2);
__decorateClass([
  state()
], XBuildifyGlobalSettings.prototype, "_form", 2);
XBuildifyGlobalSettings = __decorateClass([
  customElement("x-buildify-global-settings")
], XBuildifyGlobalSettings);
var XBuildifyGlobalSettingsColorPicker = class extends FormField {
  render() {
    return html`
      <x-flex space="xs">
        ${this.error ? html` <x-text tone="error"> ${this.error} </x-text> ` : nothing} ${this._colors()}
      </x-flex>
    `;
  }
  _colors() {
    return html`
      <x-flex space="xs">
        ${repeat(
      this.value.value ?? [],
      (colorCode) => colorCode,
      (colorCode, i) => html`
              <x-flex horizontal crossaxis-center space="xs">
                <x-input
                  flex
                  type="color"
                  @value-picked=${(e) => {
        this.value.value[i] = e.detail.value;
        this._commitValue();
      }}
                  .value=${colorCode}
                ></x-input>
                <x-button
                  mode="icon"
                  icon=${this.configuration.icons.delete}
                  tone="error"
                  @click=${() => {
        this.value.value.splice(i, 1);
        this._commitValue();
      }}
                ></x-button>
              </x-flex>
            `
    )}
        <x-button
          mode="outline"
          icon=${this.configuration.icons.add}
          @click=${() => {
      if (!this.value.value)
        this.value.value = [];
      this.value.value.push("transparent");
      this._commitValue();
    }}
        >
          Neue Farbe hinzufügen
        </x-button>
      </x-flex>
    `;
  }
  get configuration() {
    return {
      icons: {
        delete: "delete",
        add: "add"
      }
    };
  }
  _commitValue() {
    this.value.next(this.value.value);
    this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
  }
};
XBuildifyGlobalSettingsColorPicker = __decorateClass([
  customElement("x-buildify-global-settings-color-picker")
], XBuildifyGlobalSettingsColorPicker);
var XBuildifyGlobalSettingsSpacingPicker = class extends FormField {
  render() {
    return html`
      <x-flex space="xs">
        ${this.error ? html` <x-text tone="error"> ${this.error} </x-text> ` : nothing} ${this._spacings()}
      </x-flex>
    `;
  }
  _spacings() {
    return html`
      <x-flex space="xs">
        ${repeat(
      this.value.value ?? [],
      (spacingDef) => spacingDef._id,
      (spacingDef, i) => {
        const spacingParts = (spacingDef.spacing ?? "").split(" ");
        const isAllSides = spacingParts?.length === 4;
        return html`
              <x-flex space="xxs">
                <x-flex horizontal crossaxis-start space="xs">
                  <x-input
                    flex
                    placeholder="Bezeichnung"
                    @value-picked=${(e) => {
          this.value.value[i].name = e.detail.value;
          this._commitValue();
        }}
                    .value=${spacingDef.name}
                  >
                  </x-input>

                  ${isAllSides ? html`
                        <x-flex space="xs">
                          <x-buildify-spacing-picker
                            flex
                            @value-picked=${(e) => {
          spacingParts[0] = e.detail.value;
          this.value.value[i].spacing = spacingParts.join(" ");
          this._commitValue();
        }}
                            .value=${spacingParts[0]}
                            mode="advanced"
                            placeholder="Oben"
                          ></x-buildify-spacing-picker>
                          <x-buildify-spacing-picker
                            flex
                            @value-picked=${(e) => {
          spacingParts[1] = e.detail.value;
          this.value.value[i].spacing = spacingParts.join(" ");
          this._commitValue();
        }}
                            .value=${spacingParts[1]}
                            mode="advanced"
                            placeholder="Rechts"
                          ></x-buildify-spacing-picker>
                          <x-buildify-spacing-picker
                            flex
                            @value-picked=${(e) => {
          spacingParts[2] = e.detail.value;
          this.value.value[i].spacing = spacingParts.join(" ");
          this._commitValue();
        }}
                            .value=${spacingParts[2]}
                            mode="advanced"
                            placeholder="Unten"
                          ></x-buildify-spacing-picker>
                          <x-buildify-spacing-picker
                            flex
                            @value-picked=${(e) => {
          spacingParts[3] = e.detail.value;
          this.value.value[i].spacing = spacingParts.join(" ");
          this._commitValue();
        }}
                            .value=${spacingParts[3]}
                            mode="advanced"
                            placeholder="Links"
                          ></x-buildify-spacing-picker>
                        </x-flex>
                      ` : html`
                        <x-buildify-spacing-picker
                          flex
                          @value-picked=${(e) => {
          this.value.value[i].spacing = e.detail.value;
          this._commitValue();
        }}
                          .value=${spacingDef.spacing}
                          mode="advanced"
                        ></x-buildify-spacing-picker>
                      `}

                  <x-button
                    mode="icon"
                    icon=${this.configuration.icons.delete}
                    tone="error"
                    @click=${() => {
          this.value.value.splice(i, 1);
          this._commitValue();
        }}
                  ></x-button>
                </x-flex>
                <x-text
                  ${css({ color: "#333", textAlign: "center", cursor: "pointer", padding: "4px 0" })}
                  @click=${() => {
          if (isAllSides) {
            this.value.value[i].spacing = spacingParts[0];
          } else {
            this.value.value[i].spacing = `${spacingParts[0]} ${spacingParts[0]} ${spacingParts[0]} ${spacingParts[0]}`;
          }
          this._commitValue();
        }}
                >
                  ${isAllSides ? "Global" : "Einzeln"}
                </x-text>
              </x-flex>
            `;
      }
    )}
        ${this._addButton()}
      </x-flex>
    `;
  }
  _addButton() {
    return html`
      <x-button
        mode="outline"
        icon=${this.configuration.icons.add}
        @click=${() => {
      if (!this.value.value)
        this.value.value = [];
      this.value.value.push(new BuildifyGlobalSettingSpacingDefinition({}));
      this._commitValue();
    }}
      >
        Neuen Abstand hinzufügen
      </x-button>
    `;
  }
  get configuration() {
    return {
      icons: {
        delete: "delete",
        add: "add"
      }
    };
  }
  _commitValue() {
    this.value.next(this.value.value);
    this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
  }
};
XBuildifyGlobalSettingsSpacingPicker = __decorateClass([
  customElement("x-buildify-global-settings-spacing-picker")
], XBuildifyGlobalSettingsSpacingPicker);
var XBuildifyGlobalSettingsWidthPicker = class extends XBuildifyGlobalSettingsSpacingPicker {
  _addButton() {
    return html`
      <x-button
        mode="outline"
        icon=${this.configuration.icons.add}
        @click=${() => {
      if (!this.value.value)
        this.value.value = [];
      this.value.value.push(new BuildifyGlobalSettingSpacingDefinition({}));
      this._commitValue();
    }}
      >
        Neue Breite hinzufügen
      </x-button>
    `;
  }
};
XBuildifyGlobalSettingsWidthPicker = __decorateClass([
  customElement("x-buildify-global-settings-width-picker")
], XBuildifyGlobalSettingsWidthPicker);
var XBuildifyGlobalSettingsBorderRadiusPicker = class extends FormField {
  render() {
    return html`
      <x-flex space="xs">
        ${this.error ? html` <x-text tone="error"> ${this.error} </x-text> ` : nothing} ${this._borderRadius()}
      </x-flex>
    `;
  }
  _borderRadius() {
    return html`
      <x-flex space="xs">
        ${repeat(
      this.value.value ?? [],
      (borderRadiusDef) => borderRadiusDef._id,
      (borderRadiusDef, i) => html`
              <x-flex horizontal crossaxis-center space="xs">
                <x-input
                  flex
                  placeholder="Bezeichnung"
                  @value-picked=${(e) => {
        this.value.value[i].name = e.detail.value;
        this._commitValue();
      }}
                  .value=${borderRadiusDef.name}
                >
                </x-input>
                <x-input
                  flex
                  placeholder="Wert"
                  @value-picked=${(e) => {
        this.value.value[i].borderRadius = e.detail.value;
        this._commitValue();
      }}
                  .value=${borderRadiusDef.borderRadius}
                >
                </x-input>
                <x-button
                  mode="icon"
                  icon=${this.configuration.icons.delete}
                  tone="error"
                  @click=${() => {
        this.value.value.splice(i, 1);
        this._commitValue();
      }}
                ></x-button>
              </x-flex>
            `
    )}
        <x-button
          mode="outline"
          icon=${this.configuration.icons.add}
          @click=${() => {
      if (!this.value.value)
        this.value.value = [];
      this.value.value.push(new BuildifyGlobalSettingBorderRadiusDefinition({}));
      this._commitValue();
    }}
        >
          Neuen Border Radius hinzufügen
        </x-button>
      </x-flex>
    `;
  }
  get configuration() {
    return {
      icons: {
        delete: "delete",
        add: "add"
      }
    };
  }
  _commitValue() {
    this.value.next(this.value.value);
    this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
  }
};
XBuildifyGlobalSettingsBorderRadiusPicker = __decorateClass([
  customElement("x-buildify-global-settings-border-radius-picker")
], XBuildifyGlobalSettingsBorderRadiusPicker);
function xBuildifyGlobalSettingsWidthPicker(host, form, config) {
  if (form.document === void 0 || form.document === null)
    return nothing;
  return html`
    <x-flex space="xs">
      ${repeat(
    form.document.widths ?? [],
    (widthDef) => widthDef._id,
    (_widthDef, idx) => html`
            <x-flex horizontal crossaxis-center space="xs">
              <x-input flex placeholder="Bezeichnung" ${form.field("widths", idx, "name")}> </x-input>
              <x-buildify-spacing-picker
                flex
                ${form.field("widths", idx, "spacing")}
                mode="advanced"
              ></x-buildify-spacing-picker>
              <x-button
                mode="icon"
                icon=${config.icons.delete}
                tone="error"
                @click=${() => {
      form.document.widths?.splice(idx, 1);
      host.requestUpdate();
    }}
              ></x-button>
            </x-flex>
          `
  )}
      <x-button
        mode="outline"
        icon=${config.icons.add}
        @click=${() => {
    if (!form.document.widths)
      form.document.widths = [];
    form.document.widths.push(new BuildifyGlobalSettingSpacingDefinition({}));
    host.requestUpdate();
  }}
      >
        Neue Breite hinzufügen
      </x-button>
    </x-flex>
  `;
}
function xBuildifyGlobalSettingsSpacingPicker(host, form, config) {
  if (form.document === void 0 || form.document === null)
    return nothing;
  return html`
    <x-flex space="xs">
      ${repeat(
    form.document.spacings ?? [],
    (spacingDef) => spacingDef._id,
    (_spacingDef, idx) => html`
            <x-flex horizontal crossaxis-center space="xs">
              <x-input flex placeholder="Bezeichnung" ${form.field("spacings", idx, "name")}> </x-input>
              <x-buildify-spacing-picker
                flex
                ${form.field("spacings", idx, "spacing")}
                mode="advanced"
              ></x-buildify-spacing-picker>
              <x-button
                mode="icon"
                icon=${config.icons.delete}
                tone="error"
                @click=${() => {
      form.document.spacings?.splice(idx, 1);
      host.requestUpdate();
    }}
              ></x-button>
            </x-flex>
          `
  )}
      <x-button
        mode="outline"
        icon=${config.icons.add}
        @click=${() => {
    if (!form.document.spacings)
      form.document.spacings = [];
    form.document.spacings.push(new BuildifyGlobalSettingSpacingDefinition({}));
    host.requestUpdate();
  }}
      >
        Neuen Abstand hinzufügen
      </x-button>
    </x-flex>
  `;
}
function xBuildifyGlobalSettingsBorderRadiusPicker(host, form, config) {
  if (form.document === void 0 || form.document === null)
    return nothing;
  return html`
    <x-flex space="xs">
      ${repeat(
    form.document.borderRadius ?? [],
    (borderRadiusDef) => borderRadiusDef._id,
    (_borderRadiusDef, idx) => html`
            <x-flex horizontal crossaxis-center space="xs">
              <x-input flex placeholder="Bezeichnung" ${form.field("borderRadius", idx, "name")}> </x-input>
              <x-input flex placeholder="Wert" ${form.field("borderRadius", idx, "borderRadius")}> </x-input>
              <x-button
                mode="icon"
                icon=${config.icons.delete}
                tone="error"
                @click=${() => {
      form.document.borderRadius.splice(idx, 1);
      host.requestUpdate();
    }}
              ></x-button>
            </x-flex>
          `
  )}
      <x-button
        mode="outline"
        icon=${config.icons.add}
        @click=${() => {
    if (!form.document.borderRadius)
      form.document.borderRadius = [];
    form.document.borderRadius.push(new BuildifyGlobalSettingBorderRadiusDefinition({}));
    host.requestUpdate();
  }}
      >
        Neuen Border Radius hinzufügen
      </x-button>
    </x-flex>
  `;
}
var XBuildifyGlobalSettingsBorderPicker = class extends FormField {
  render() {
    return xComponents(html`
      <x-flex space="xs">
        ${this.error ? html` <x-text tone="error"> ${this.error} </x-text> ` : nothing} ${this._border()}
      </x-flex>
    `);
  }
  _border() {
    return xComponents(html`
      <x-flex space="xs">
        ${repeat(
      this.value.value ?? [],
      (borderRadiusDef) => borderRadiusDef._id,
      (borderRadiusDef, i) => {
        let borderParts = (borderRadiusDef.border ?? "  ").split(" ");
        return html`
              <x-flex horizontal crossaxis-start space="xs">
                <x-input
                  flex
                  placeholder="Bezeichnung"
                  @value-picked=${(e) => {
          this.value.value[i].name = e.detail.value;
          this._commitValue();
        }}
                  .value=${borderRadiusDef.name}
                >
                </x-input>
                <x-flex space="xs">
                  <x-buildify-spacing-picker
                    mode="advanced"
                    placeholder="Breite"
                    @value-picked=${(e) => {
          borderParts[0] = e.detail.value;
          borderRadiusDef.border = borderParts.join(" ");
          this._commitValue();
        }}
                    .value=${borderParts[0]}
                  ></x-buildify-spacing-picker>
                  <x-dropdown-selection
                    placeholder="Rand-Typ"
                    .selectables=${["solid", "dashed", "dotted", "double"]}
                    .value=${borderParts[1]}
                    @value-picked=${(e) => {
          borderParts[1] = e.detail.value;
          borderRadiusDef.border = borderParts.join(" ");
          this._commitValue();
        }}
                  ></x-dropdown-selection>
                  <x-buildify-color-picker
                    placeholder="Farbe"
                    .value=${borderParts[2]}
                    @value-picked=${(e) => {
          borderParts[2] = e.detail.value;
          borderRadiusDef.border = borderParts.join(" ");
          this._commitValue();
        }}
                  ></x-buildify-color-picker>
                </x-flex>

                <x-button
                  mode="icon"
                  icon=${this.configuration.icons.delete}
                  tone="error"
                  @click=${() => {
          this.value.value.splice(i, 1);
          this._commitValue();
        }}
                ></x-button>
              </x-flex>
            `;
      }
    )}
        <x-button
          mode="outline"
          icon=${this.configuration.icons.add}
          @click=${() => {
      if (!this.value.value)
        this.value.value = [];
      this.value.value.push(new BuildifyGlobalSettingBorderDefinition({}));
      this._commitValue();
    }}
        >
          Neuen Border hinzufügen
        </x-button>
      </x-flex>
    `);
  }
  get configuration() {
    return {
      icons: {
        delete: "delete",
        add: "add"
      }
    };
  }
  _commitValue() {
    this.value.next(this.value.value);
    this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
  }
};
XBuildifyGlobalSettingsBorderPicker = __decorateClass([
  customElement("x-buildify-global-settings-border-picker")
], XBuildifyGlobalSettingsBorderPicker);
export {
  ItemShadow,
  XBuildifyGlobalSettings,
  XBuildifyGlobalSettingsBorderPicker,
  XBuildifyGlobalSettingsBorderRadiusPicker,
  XBuildifyGlobalSettingsColorPicker,
  XBuildifyGlobalSettingsSpacingPicker,
  XBuildifyGlobalSettingsWidthPicker,
  xBuildifyGlobalSettingsBorderRadiusPicker,
  xBuildifyGlobalSettingsSpacingPicker,
  xBuildifyGlobalSettingsWidthPicker
};
